/**
 * Module-to-Dashboard Section Mapping
 * ====================================
 * Maps each purchasable module to the dashboard sections it unlocks.
 * Used by the dashboard to gate content and show teasers for disabled modules.
 */

// All available modules a business can enable
export const ALL_MODULES = [
  { id: 'operations', name: 'Live Operations Dashboard', shortName: 'Operations', description: 'Real-time business metrics and operations monitoring', icon: 'Activity', color: '#3b82f6', gradient: 'from-blue-500 to-indigo-500' },
  { id: 'financial', name: 'Financial Management', shortName: 'Financial', description: 'P&L, cash flow, projections, and tax compliance', icon: 'DollarSign', color: '#10b981', gradient: 'from-emerald-500 to-[#0066CC]' },
  { id: 'marketing', name: 'Marketing Intelligence', shortName: 'Marketing', description: 'Social media, reviews, campaigns, and customer insights', icon: 'Megaphone', color: '#ec4899', gradient: 'from-pink-500 to-rose-500' },
  { id: 'customer', name: 'Customer Analytics', shortName: 'Customer', description: 'Customer behavior, loyalty, and segmentation', icon: 'Users', color: '#06b6d4', gradient: 'from-[#007AFF] to-blue-500' },
  { id: 'staff', name: 'Staff & Rostering', shortName: 'Staff', description: 'Labor costs, scheduling, and performance', icon: 'UserCheck', color: '#8b5cf6', gradient: 'from-violet-500 to-purple-500' },
  { id: 'market_intel', name: 'Market Intelligence', shortName: 'Market Intel', description: 'Competitive benchmarking and industry insights', icon: 'Search', color: '#f59e0b', gradient: 'from-amber-500 to-orange-500' },
  { id: 'inventory', name: 'Inventory Management', shortName: 'Inventory', description: 'Stock levels, waste tracking, and ordering', icon: 'Package', color: '#64748b', gradient: 'from-slate-500 to-gray-600' },
  { id: 'projections', name: 'Projections Builder', shortName: 'Projections', description: 'Financial forecasting and scenario planning', icon: 'TrendingUp', color: '#14b8a6', gradient: 'from-[#0066CC] to-emerald-500' },
  { id: 'compliance', name: 'Compliance & Safety', shortName: 'Compliance', description: 'Regulatory compliance monitoring and reporting', icon: 'Shield', color: '#ef4444', gradient: 'from-red-500 to-rose-500' },
];

// Which dashboard sections each module unlocks
// Sections not listed here are ALWAYS visible (core sections)
export const MODULE_SECTION_MAP = {
  operations: {
    sections: ['live-metrics', 'ai-narrative', 'predictive-alerts'],
    teaserData: {
      title: 'Live Operations Intelligence',
      valueProps: [
        'Real-time operational metrics across all sites',
        'AI daily briefings — what happened, why, and what to do',
        'Predictive alerts 24-48 hrs ahead with root-cause analysis',
        'Throughput, wait and response-time monitoring',
      ],
      benchmarks: [
        { label: 'Issue Resolution', value: '12% faster', industry: 'vs manual tracking' },
        { label: 'Alert Lead Time', value: '24-48 hrs', industry: 'ahead of impact' },
        { label: 'Labour Cost %', value: 'On target', industry: 'tracked live' },
      ],
      impact: 'Businesses with Operations Intelligence see 12% faster issue resolution and $4,200/mo in prevented losses on average.',
    },
  },
  financial: {
    sections: ['financial', 'revenue-leakage', 'executive-summary', 'economic-pulse'],
    teaserData: {
      title: 'Financial Intelligence',
      valueProps: [
        'P&L dashboards with real-time cost tracking',
        'Revenue leakage detection — find where money is slipping',
        'Board-ready executive summaries (weekly/monthly)',
        'Australian economic pulse affecting your business',
      ],
      benchmarks: [
        { label: 'Cost Ratio', value: 'On target', industry: 'tracked live' },
        { label: 'Revenue Growth', value: '+8% MoM', industry: 'Industry Avg: +4.2%' },
        { label: 'Profit Margin', value: '18%+', industry: 'Industry Avg: 14.6%' },
      ],
      impact: 'Financial Intelligence users recover an average of $8,400/month in identified leakage and reduce cost overruns by 22%.',
    },
  },
  marketing: {
    sections: ['cx-scorecard'],
    teaserData: {
      title: 'Marketing & CX Intelligence',
      valueProps: [
        'Customer experience scorecard with NPS tracking',
        'Review sentiment analysis across Google and social platforms',
        'Satisfaction by time-of-day and staff correlation',
        'Campaign ROI tracking and attribution',
      ],
      benchmarks: [
        { label: 'NPS Target', value: '> 50', industry: 'Industry Avg: 38' },
        { label: 'Review Rating', value: '4.5+/5', industry: 'Industry Avg: 4.1' },
        { label: 'Response Rate', value: '> 85%', industry: 'Industry Avg: 52%' },
      ],
      impact: 'Businesses tracking CX metrics see 23% higher customer retention and a 0.4-point average rating improvement within 90 days.',
    },
  },
  customer: {
    sections: ['wins', 'opportunities', 'activity'],
    teaserData: {
      title: 'Customer Analytics',
      valueProps: [
        'Win tracking — celebrate revenue milestones and team achievements',
        'Opportunity detection with likelihood scoring',
        'Customer behavior patterns and segmentation',
        'Churn prediction and retention workflows',
      ],
      benchmarks: [
        { label: 'Avg Transaction', value: '$22.50', industry: 'Industry Avg: $18.80' },
        { label: 'Repeat Rate', value: '> 45%', industry: 'Industry Avg: 32%' },
        { label: 'Daily Transactions', value: '580+', industry: 'Industry Avg: 420' },
      ],
      impact: 'Customer Analytics helps identify $6,600/month in missed upsell opportunities and increases repeat visit rate by 18%.',
    },
  },
  staff: {
    sections: ['staff-roi', 'knowledge-transfer', 'performance'],
    teaserData: {
      title: 'Staff & Rostering Intelligence',
      valueProps: [
        'Staff ROI scoring — revenue impact per team member',
        'Cross-location knowledge transfer playbooks',
        'Shift-level performance correlation with revenue',
        'Training gap identification and scheduling optimization',
      ],
      benchmarks: [
        { label: 'Top Performer Revenue Impact', value: '+$8.40', industry: 'Avg variance: $5.20' },
        { label: 'Attendance Target', value: '> 95%', industry: 'Industry Avg: 89%' },
        { label: 'Upsell Rate (Top)', value: '> 50%', industry: 'Industry Avg: 38%' },
      ],
      impact: 'Staff Intelligence users see 15% improvement in revenue per labour hour through targeted coaching and a 40% reduction in scheduling-related losses.',
    },
  },
  market_intel: {
    sections: ['market', 'benchmark-alerts'],
    teaserData: {
      title: 'Market Intelligence',
      valueProps: [
        'Competitive benchmarking against local and national operators',
        'Automated alerts when you fall below industry benchmarks',
        'Pricing intelligence and competitor comparison',
        'Market trend tracking and opportunity sizing',
      ],
      benchmarks: [
        { label: 'Your Performance vs Market', value: 'Index: 112', industry: 'Above market average' },
        { label: 'Price Competitiveness', value: 'Index: 102', industry: 'Balanced vs competitors' },
        { label: 'Market Share (Local)', value: '18.5%', industry: 'Top 3 in 4/5 areas' },
      ],
      impact: 'Market Intelligence subscribers identify competitive threats 3 weeks earlier and adjust pricing 2x faster than non-subscribers.',
    },
  },
  projections: {
    sections: ['what-if', 'planning'],
    teaserData: {
      title: 'Projections & Scenario Builder',
      valueProps: [
        'What-if scenario modeler — test changes before committing',
        '"Add 1 staff" / "Improve upsell 5%" impact projections',
        'Financial forecasting with seasonal adjustments',
        'Growth planning and goal tracking',
      ],
      benchmarks: [
        { label: 'Scenario Accuracy', value: '±8%', industry: 'Based on 12-month data' },
        { label: 'Planning Horizon', value: '12 months', industry: 'With quarterly refresh' },
        { label: 'ROI on Changes', value: 'Pre-calculated', industry: 'Before you commit' },
      ],
      impact: 'Businesses using Projections make 35% more confident expansion decisions and avoid an average of $12,000 in poorly-timed investments.',
    },
  },
  inventory: {
    sections: [],
    teaserData: {
      title: 'Inventory Management',
      valueProps: [
        'Real-time stock level monitoring across locations',
        'Waste tracking with cost attribution',
        'Automated reorder point alerts',
        'Supplier performance scoring and comparison',
      ],
      benchmarks: [
        { label: 'Waste Target', value: '< 3%', industry: 'Industry Avg: 5.2%' },
        { label: 'Stock Accuracy', value: '> 98%', industry: 'Industry Avg: 91%' },
        { label: 'Order Efficiency', value: '< 24hrs', industry: 'From alert to delivery' },
      ],
      impact: 'Inventory Management reduces stock waste by 38% and saves an average of $3,200/month in overordering and spoilage.',
    },
  },
  compliance: {
    sections: [],
    teaserData: {
      title: 'Compliance & Safety',
      valueProps: [
        'Automated compliance checklist tracking',
        'Health & safety score monitoring',
        'Staff certification and training tracking',
        'Regulatory audit preparation reports',
      ],
      benchmarks: [
        { label: 'Compliance Score', value: '> 95%', industry: 'Target: Zero non-conformances' },
        { label: 'Audit Readiness', value: 'Always', industry: 'Real-time tracking' },
        { label: 'Training Currency', value: '100%', industry: 'All certs up to date' },
      ],
      impact: 'Compliance module users pass 96% of surprise audits first time and reduce compliance-related incidents by 74%.',
    },
  },
};

// Sections that are ALWAYS visible regardless of modules (core platform)
export const CORE_SECTIONS = [
  'insights',        // AI Intelligence Hero / Greeting
  'connected-sources',
  'module-control',  // Module toggle panel
  'seasonal',
  'splh-heatmap',
  'location-comparison',
  'kpis',
  'concerns',
  'tasks',
  'upsell',
  'quick-stats',
  'quick-links',
];

/**
 * Check if a dashboard section is unlocked by the given enabled modules
 */
export const isSectionUnlocked = (sectionId, enabledModules) => {
  // Core sections are always visible
  if (CORE_SECTIONS.includes(sectionId)) return true;
  
  // Check if any enabled module unlocks this section
  for (const moduleId of enabledModules) {
    const moduleConfig = MODULE_SECTION_MAP[moduleId];
    if (moduleConfig && moduleConfig.sections.includes(sectionId)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Get the teaser data for a locked section
 */
export const getTeaserForSection = (sectionId) => {
  for (const [moduleId, config] of Object.entries(MODULE_SECTION_MAP)) {
    if (config.sections.includes(sectionId)) {
      const moduleInfo = ALL_MODULES.find(m => m.id === moduleId);
      return {
        moduleId,
        moduleName: moduleInfo?.name || moduleId,
        moduleColor: moduleInfo?.color || '#6366f1',
        moduleGradient: moduleInfo?.gradient || 'from-indigo-500 to-purple-500',
        ...config.teaserData,
      };
    }
  }
  return null;
};

export default MODULE_SECTION_MAP;
